import { RegisterOptions } from "react-hook-form";

import {
  TemplateVersion,
  TemplateDeployment,
  TemplateDeploymentUpdateInputPair,
  BlueprintInput,
  BlueprintInputType,
  BlueprintInputValidations,
} from "types/generated";

import { SecretFieldAction, VersionTemplateFormFields } from "./types";

type BuildUpdateInputsPayloadParams = {
  formInputs: VersionTemplateFormFields["inputs"];
  secretInputIds: Set<string>;
  existingSecretIds: Set<string>;
  secretFieldActions: Map<string, SecretFieldAction>;
};

/**
 * Builds the input pair for a secret field in update mode.
 * Handles NEW secrets (added to template) vs EXISTING secrets (already in deployment).
 */
const buildSecretInputPayload = (
  key: string,
  value: unknown,
  existingSecretIds: Set<string>,
  secretFieldActions: Map<string, SecretFieldAction>
): TemplateDeploymentUpdateInputPair => {
  const secretExistsInDeployment = existingSecretIds.has(key);

  // For NEW secrets (not in deployment), treat like create mode
  if (!secretExistsInDeployment) {
    return {
      id: key,
      value: value?.toString() ?? "",
      // No keepOldValue - this is a new secret being added
    } as TemplateDeploymentUpdateInputPair;
  }

  // For EXISTING secrets, handle the action
  const action = secretFieldActions.get(key) ?? SecretFieldAction.KEEP;

  if (action === SecretFieldAction.KEEP) {
    // Keep existing value: send keepOldValue flag
    return { id: key, value: "", keepOldValue: true };
  }

  if (action === SecretFieldAction.RESET) {
    // Reset = field exists but has an empty value ("")
    return { id: key, value: "", keepOldValue: false };
  }

  // action === CHANGE: use the form value the user typed
  return { id: key, value: value?.toString() ?? "", keepOldValue: false };
};

/**
 * Builds the inputs payload for updating a deployment.
 * Handles different logic for secret vs non-secret fields, and for existing vs new secrets.
 */
export const buildUpdateInputsPayload = ({
  formInputs,
  secretInputIds,
  existingSecretIds,
  secretFieldActions,
}: BuildUpdateInputsPayloadParams): TemplateDeploymentUpdateInputPair[] => {
  return Object.entries(formInputs)
    .filter(([key]) => {
      if (secretInputIds.has(key)) {
        const action = secretFieldActions.get(key);
        // DELETE omits the field from the payload entirely (field no longer exists).
        return action !== SecretFieldAction.DELETE;
      }
      return true;
    })
    .map(([key, value]) => {
      const isSecret = secretInputIds.has(key);

      if (isSecret) {
        return buildSecretInputPayload(key, value, existingSecretIds, secretFieldActions);
      }

      // Non-secret fields: always update (no keepOldValue property)
      return {
        id: key,
        value: value?.toString() as string,
      } as TemplateDeploymentUpdateInputPair;
    });
};

type BuildDefaultValuesParams = {
  inputs: BlueprintInput[];
  version: TemplateVersion;
  deployment?: TemplateDeployment;
  isUpdateMode: boolean;
  existingSecretIds: Set<string>;
};

/**
 * Builds the default form values for the deployment form.
 * Handles different logic for create vs update mode, boolean fields, and secret fields.
 */
export const buildDefaultValues = ({
  inputs,
  version,
  deployment,
  isUpdateMode,
  existingSecretIds,
}: BuildDefaultValuesParams): VersionTemplateFormFields => {
  const inputDefaults = inputs.reduce(
    (acc, input) => {
      const deploymentInput =
        isUpdateMode && deployment && deployment.inputs.find((di) => di.id === input.id);
      const value = deploymentInput ? deploymentInput.value : input.default;

      if (input.type === BlueprintInputType.Boolean) {
        acc[input.id] = value === "true";
      } else if (input.type === BlueprintInputType.Secret && isUpdateMode) {
        // In update mode, secret values aren't returned by the API.
        // Initialize as empty string if the secret exists in the deployment,
        // otherwise use the default value (for new secrets added to the template).
        acc[input.id] = existingSecretIds.has(input.id) ? "" : input.default;
      } else {
        acc[input.id] = value;
      }
      return acc;
    },
    {} as VersionTemplateFormFields["inputs"]
  );

  return {
    name: deployment?.name || "",
    description: deployment?.description || "",
    space: version.space.id,
    inputs: inputDefaults,
    versionId: deployment?.templateVersion.id || version.id,
  };
};

/**
 * Converts BlueprintInputValidations to React Hook Form validation rules.
 * Handles all validation types: required, length constraints, numeric constraints, pattern matching, etc.
 */
export const buildValidationRules = (
  validations: BlueprintInputValidations | null | undefined
): RegisterOptions => {
  if (!validations) {
    return {};
  }

  const rules: RegisterOptions = {};

  if (validations.required) {
    rules.required = "This field is required";
  }

  if (validations.minLength !== null && validations.minLength !== undefined) {
    rules.minLength = {
      value: validations.minLength,
      message: `Minimum length is ${validations.minLength}`,
    };
  }

  if (validations.maxLength !== null && validations.maxLength !== undefined) {
    rules.maxLength = {
      value: validations.maxLength,
      message: `Maximum length is ${validations.maxLength}`,
    };
  }

  if (validations.lengthEqual !== null && validations.lengthEqual !== undefined) {
    rules.validate = {
      ...rules.validate,
      lengthEqual: (value: string) =>
        value?.length === validations.lengthEqual ||
        `Length must be exactly ${validations.lengthEqual}`,
    };
  }

  if (validations.pattern) {
    rules.pattern = {
      value: new RegExp(validations.pattern),
      message: "Invalid format",
    };
  }

  if (validations.greaterThan !== null && validations.greaterThan !== undefined) {
    rules.validate = {
      ...rules.validate,
      greaterThan: (value: number) =>
        Number(value) > validations.greaterThan! || `Must be greater than ${validations.greaterThan}`,
    };
  }

  if (validations.greaterThanOrEqual !== null && validations.greaterThanOrEqual !== undefined) {
    rules.min = {
      value: validations.greaterThanOrEqual,
      message: `Must be at least ${validations.greaterThanOrEqual}`,
    };
  }

  if (validations.lessThan !== null && validations.lessThan !== undefined) {
    rules.validate = {
      ...rules.validate,
      lessThan: (value: number) =>
        Number(value) < validations.lessThan! || `Must be less than ${validations.lessThan}`,
    };
  }

  if (validations.lessThanOrEqual !== null && validations.lessThanOrEqual !== undefined) {
    rules.max = {
      value: validations.lessThanOrEqual,
      message: `Must be at most ${validations.lessThanOrEqual}`,
    };
  }

  if (validations.notEqual !== null && validations.notEqual !== undefined) {
    rules.validate = {
      ...rules.validate,
      notEqual: (value: number) =>
        Number(value) !== validations.notEqual || `Must not be equal to ${validations.notEqual}`,
    };
  }

  if (validations.step !== null && validations.step !== undefined) {
    rules.validate = {
      ...rules.validate,
      step: (value: number) =>
        Number(value) % validations.step! === 0 ||
        `Must be a multiple of ${validations.step}`,
    };
  }

  return rules;
};
